/**
 * Normalize notification action_url for navigation.
 *
 * Backends often store absolute URLs captured at creation time (e.g. http://localhost:5176/jobs/...).
 * In production, those must not open localhost — use the path on the current app origin instead.
 */

import type { Notification } from '../api/notifications';

export type ResolvedNotificationAction =
  | { kind: 'internal'; path: string }
  | { kind: 'external'; url: string };

const JOB_ID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function extractUuidFromPathname(pathname: string): string | undefined {
  const segments = pathname.split('/').filter(Boolean);
  const last = segments[segments.length - 1];
  if (last && JOB_ID_RE.test(last)) return last;
  return undefined;
}

function extractJobIdFromJobsPath(pathname: string): string | undefined {
  return extractUuidFromPathname(pathname);
}

/**
 * Prefer context.job_id; otherwise parse UUID from action_url paths like /jobs/:id or /jobs/jobs/:id.
 */
export function extractJobIdFromNotification(notification: Notification): string | undefined {
  const raw = notification.context?.job_id;
  if (typeof raw === 'string' && raw.trim()) return raw.trim();

  if (notification.action_url) {
    const resolved = resolveNotificationActionUrl(notification.action_url);
    if (resolved.kind === 'internal') {
      const pathOnly = resolved.path.split('?')[0];
      const id = extractJobIdFromJobsPath(pathOnly);
      if (id) return id;
    }
  }
  return undefined;
}

/**
 * Report / proposal notifications: prefer context.report_id; else last UUID in action_url (e.g. …/proposals-management/current/{id}).
 */
export function extractReportIdFromNotification(notification: Notification): string | undefined {
  const c = notification.context;
  const raw = c?.report_id ?? c?.reportId;
  if (typeof raw === 'string' && raw.trim()) return raw.trim();

  if (notification.action_url) {
    const resolved = resolveNotificationActionUrl(notification.action_url);
    if (resolved.kind === 'internal') {
      const pathOnly = resolved.path.split('?')[0];
      const id = extractUuidFromPathname(pathOnly);
      if (id) return id;
    }
  }
  return undefined;
}

/** Keystone RFP: Proposal Management list opens a report via query (see ProposalListPage). */
export function buildProposalManagementReportUrl(reportId: string): string {
  const p = new URLSearchParams();
  p.set('tab', 'list');
  p.set('reportId', reportId);
  return `/rfp/proposals-management?${p.toString()}`;
}

/**
 * Report notifications often ship action_url like /rfp/proposals-management/current/{report_id} which is not a real route.
 * Navigate to Proposal Management with ?reportId= instead of /reports/:id.
 */
export function getProposalManagementPathFromReportNotification(
  notification: Notification
): string | null {
  const reportId = extractReportIdFromNotification(notification);
  if (!reportId) return null;

  const { type, action_url } = notification;

  if (type === 'report_comment' || type === 'report_status_change') {
    return buildProposalManagementReportUrl(reportId);
  }

  if (action_url) {
    const resolved = resolveNotificationActionUrl(action_url);
    if (resolved.kind === 'internal') {
      const pathOnly = resolved.path.split('?')[0];
      if (/proposals-management/i.test(pathOnly)) {
        return buildProposalManagementReportUrl(reportId);
      }
    }
  }

  return null;
}

/** Jobs deep-link or Proposal Management report deep-link (preferred over raw action_url). */
export function getInternalNotificationPath(notification: Notification): string | null {
  const jobPath = getDataJobsPathFromJobNotification(notification);
  if (jobPath) return jobPath;
  const proposalPath = getProposalManagementPathFromReportNotification(notification);
  if (proposalPath) return proposalPath;
  return null;
}

function getFallbackRouteForNotificationType(notification: Notification): string | null {
  const { type, context } = notification;

  switch (type) {
    case 'job_completion':
    case 'job_failed': {
      const jid = typeof context?.job_id === 'string' ? context.job_id.trim() : '';
      if (jid) return buildDataJobsListUrl({ jobId: jid });
      return buildDataJobsListUrl('all');
    }

    case 'report_comment':
      if (context?.report_id) {
        return `/reports/${context.report_id}#comments`;
      }
      return '/reports';

    case 'report_status_change':
      if (context?.report_id) {
        return `/reports/${context.report_id}`;
      }
      return '/reports';

    case 'new_opportunity':
      if (context?.opportunity_id) {
        return `/rfp/external-data?tab=opportunities&id=${context.opportunity_id}`;
      }
      return '/rfp/external-data?tab=opportunities';

    case 'keyword_recommendation':
      return '/rfp/proposals-management?tab=keywords';

    case 'industry_news_digest':
      return '/rfp/external-data?tab=news';

    default:
      return null;
  }
}

/**
 * Single entry: internal handled paths first, then action_url, then type-based fallback.
 */
export function navigateToNotification(notification: Notification): void {
  const internal = getInternalNotificationPath(notification);
  if (internal) {
    navigateNotificationPath(internal);
    return;
  }
  if (notification.action_url) {
    navigateFromNotificationActionUrl(notification.action_url);
    return;
  }
  const fallback = getFallbackRouteForNotificationType(notification);
  if (fallback) {
    navigateNotificationPath(fallback);
  }
}

function isLocalDevHostname(hostname: string): boolean {
  const h = hostname.toLowerCase();
  return h === 'localhost' || h === '127.0.0.1' || h === '[::1]' || h.endsWith('.local');
}

/**
 * Resolve action_url to either an in-app path or a true external URL.
 */
export function resolveNotificationActionUrl(actionUrl: string): ResolvedNotificationAction {
  const trimmed = actionUrl.trim();
  if (!trimmed) {
    return { kind: 'internal', path: '/' };
  }

  if (!trimmed.startsWith('http://') && !trimmed.startsWith('https://')) {
    const path = trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
    return { kind: 'internal', path };
  }

  try {
    const parsed = new URL(trimmed);
    const currentOrigin = typeof window !== 'undefined' ? window.location.origin : '';
    const targetOrigin = `${parsed.protocol}//${parsed.host}`;
    const pathWithQuery = `${parsed.pathname}${parsed.search}${parsed.hash}` || '/';

    // Same origin as current app — SPA internal
    if (currentOrigin && targetOrigin === currentOrigin) {
      return { kind: 'internal', path: pathWithQuery };
    }

    // Dev hostnames (localhost, etc.) — treat as same-app path; DB was populated from dev
    if (isLocalDevHostname(parsed.hostname)) {
      return { kind: 'internal', path: pathWithQuery };
    }

    // Optional: VITE_APP_ORIGIN matches stored URL (staging vs prod canonical)
    const canonical =
      typeof import.meta !== 'undefined' && (import.meta as ImportMeta & { env?: Record<string, string> }).env?.VITE_APP_ORIGIN;
    if (canonical) {
      try {
        if (targetOrigin === new URL(canonical).origin) {
          return { kind: 'internal', path: pathWithQuery };
        }
      } catch {
        /* ignore */
      }
    }

    return { kind: 'external', url: trimmed };
  } catch {
    const path = trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
    return { kind: 'internal', path };
  }
}

export type DataJobsListOptions = {
  /** Prefills JobList search (matches job id, type, status text). */
  jobId?: string;
  status?: 'failed' | 'completed' | 'all';
};

/**
 * Data Pipeline → Jobs tab. Use `jobId` to deep-link without status filter; otherwise optional status.
 * Backward-compatible: buildDataJobsListUrl('completed') still works.
 */
export function buildDataJobsListUrl(
  statusOrOptions?: 'failed' | 'completed' | 'all' | DataJobsListOptions
): string {
  let jobId: string | undefined;
  let status: 'failed' | 'completed' | 'all' | undefined;

  if (typeof statusOrOptions === 'string' || statusOrOptions === undefined) {
    status = statusOrOptions ?? 'all';
  } else {
    jobId = statusOrOptions.jobId;
    status = statusOrOptions.status;
  }

  const p = new URLSearchParams();
  p.set('tab', 'jobs');
  p.set('sort', 'created_at');
  p.set('order', 'desc');
  if (jobId) {
    p.set('q', jobId);
  } else if (status === 'failed' || status === 'completed') {
    p.set('status', status);
  }
  return `/data?${p.toString()}`;
}

/**
 * Job notifications: backend stores action_url like {base}/jobs/{id} (see notification_helpers).
 * Navigate to Data Pipeline → Jobs with search = job id, not /jobs/:id (404 / double-path issues).
 */
export function getDataJobsPathFromJobNotification(notification: Notification): string | null {
  const { type, action_url } = notification;
  const jobId = extractJobIdFromNotification(notification);

  if (type === 'job_completion' || type === 'job_failed') {
    if (jobId) return buildDataJobsListUrl({ jobId });
    return buildDataJobsListUrl('all');
  }

  if (action_url) {
    const resolved = resolveNotificationActionUrl(action_url);
    if (resolved.kind !== 'internal') return null;
    const pathOnly = resolved.path.split('?')[0];
    // /jobs/:id or accidental /jobs/jobs/:id
    if (
      /^\/jobs\/[^/]+/.test(pathOnly) ||
      /^\/jobs\/jobs\//.test(pathOnly) ||
      pathOnly === '/jobs'
    ) {
      if (jobId) return buildDataJobsListUrl({ jobId });
      return buildDataJobsListUrl('all');
    }
  }

  return null;
}

/**
 * SPA navigation: App.tsx listens for `navigate` and updates pathname + history (do not pushState here).
 */
export function navigateNotificationPath(route: string): void {
  const path = route.startsWith('/') ? route : `/${route}`;
  window.dispatchEvent(new CustomEvent('navigate', { detail: { path } }));
}

/**
 * Perform navigation from a raw action_url (handles dev absolute URLs + external links).
 */
export function navigateFromNotificationActionUrl(actionUrl: string): void {
  const resolved = resolveNotificationActionUrl(actionUrl);
  if (resolved.kind === 'internal') {
    navigateNotificationPath(resolved.path);
  } else {
    window.open(resolved.url, '_blank', 'noopener,noreferrer');
  }
}
